import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Calendar, DollarSign, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../../theme/netflixTheme';
import toast from 'react-hot-toast';

interface PaymentRecord {
  id: string;
  amount: number;
  plan: string;
  started_at: string;
  expires_at: string;
  status: string;
  payment_status: string;
  payment_method: string;
  order_uuid: string;
  paid_at: string;
}

export function BillingScreen({ onBack }: { onBack: () => void }) {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'history' | 'current'>('history');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/subscription/history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPayments(data.data || []);
      } else {
        toast.error('Erro ao carregar histórico de pagamentos');
      }
    } catch (err) {
      console.error('Erro ao buscar pagamentos:', err);
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return NETFLIX_COLORS.primary;
      case 'expired':
        return '#EF4444';
      case 'cancelled':
        return '#F97316';
      default:
        return NETFLIX_COLORS.textSecondary;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativa';
      case 'expired':
        return 'Expirada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const getPaymentStatusIcon = (status: string) => {
    if (status === 'paid') {
      return <CheckCircle size={16} color={NETFLIX_COLORS.primary} />;
    } else if (status === 'pending') {
      return <Clock size={16} color="#F59E0B" />;
    } else {
      return <AlertCircle size={16} color="#EF4444" />;
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={onBack}>
          <ArrowLeft size={24} color={NETFLIX_COLORS.textPrimary} />
        </button>
        <h1 style={titleStyle}>Histórico de Assinatura</h1>
      </div>

      {/* Tabs */}
      <div style={tabsContainerStyle}>
        <button
          style={{
            ...tabButtonStyle,
            ...(activeTab === 'history' && {
              borderBottomColor: NETFLIX_COLORS.primary,
              color: NETFLIX_COLORS.primary,
            }),
          }}
          onClick={() => setActiveTab('history')}
        >
          Histórico
        </button>
        <button
          style={{
            ...tabButtonStyle,
            ...(activeTab === 'current' && {
              borderBottomColor: NETFLIX_COLORS.primary,
              color: NETFLIX_COLORS.primary,
            }),
          }}
          onClick={() => setActiveTab('current')}
        >
          Assinatura Atual
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div style={loadingStyle}>
          <p>Carregando...</p>
        </div>
      ) : (
        <>
          {activeTab === 'history' && (
            <div style={contentStyle}>
              {payments.length === 0 ? (
                <div style={emptyStyle}>
                  <p>Nenhum pagamento registrado</p>
                </div>
              ) : (
                <div style={tableContainerStyle}>
                  {payments.map((payment) => (
                    <div key={payment.id} style={paymentCardStyle}>
                      <div style={cardHeaderStyle}>
                        <div>
                          <p style={planNameStyle}>{payment.plan.toUpperCase()}</p>
                          <p style={dateStyle}>
                            {formatDate(payment.started_at)} até {formatDate(payment.expires_at)}
                          </p>
                        </div>
                        <div style={statusBadgeStyle(getStatusColor(payment.status))}>
                          {getStatusLabel(payment.status)}
                        </div>
                      </div>

                      <div style={cardDetailsStyle}>
                        <div style={detailItemStyle}>
                          <DollarSign size={16} color={NETFLIX_COLORS.primary} />
                          <div>
                            <p style={detailLabelStyle}>Valor Pago</p>
                            <p style={detailValueStyle}>{formatCurrency(payment.amount)}</p>
                          </div>
                        </div>

                        <div style={detailItemStyle}>
                          <Calendar size={16} color={NETFLIX_COLORS.primary} />
                          <div>
                            <p style={detailLabelStyle}>Método</p>
                            <p style={detailValueStyle}>{payment.payment_method.toUpperCase()}</p>
                          </div>
                        </div>

                        <div style={detailItemStyle}>
                          {getPaymentStatusIcon(payment.payment_status)}
                          <div>
                            <p style={detailLabelStyle}>Status</p>
                            <p style={detailValueStyle}>
                              {payment.payment_status === 'paid'
                                ? 'Pago'
                                : payment.payment_status === 'pending'
                                ? 'Pendente'
                                : 'Falhou'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div style={cardFooterStyle}>
                        <p style={uuidStyle}>ID: {payment.order_uuid}</p>
                        <button
                          style={downloadButtonStyle}
                          onClick={() => toast.success('Download da nota fiscal em desenvolvimento')}
                        >
                          <Download size={14} />
                          Nota Fiscal
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'current' && (
            <div style={contentStyle}>
              {payments.length > 0 && payments[0].status === 'active' ? (
                <div style={currentSubscriptionStyle}>
                  <div style={currentHeaderStyle}>
                    <CheckCircle size={32} color={NETFLIX_COLORS.primary} />
                    <h2 style={currentTitleStyle}>Assinatura Ativa</h2>
                  </div>

                  <div style={currentDetailsStyle}>
                    <div style={currentDetailStyle}>
                      <p style={currentLabelStyle}>Plano</p>
                      <p style={currentValueStyle}>{payments[0].plan.toUpperCase()}</p>
                    </div>

                    <div style={currentDetailStyle}>
                      <p style={currentLabelStyle}>Válida até</p>
                      <p style={currentValueStyle}>{formatDate(payments[0].expires_at)}</p>
                    </div>

                    <div style={currentDetailStyle}>
                      <p style={currentLabelStyle}>Valor mensal</p>
                      <p style={currentValueStyle}>{formatCurrency(payments[0].amount)}</p>
                    </div>

                    <div style={currentDetailStyle}>
                      <p style={currentLabelStyle}>Renovação</p>
                      <p style={currentValueStyle}>Automática</p>
                    </div>
                  </div>

                  <div style={actionButtonsStyle}>
                    <button style={secondaryButtonStyle}>Alterar Plano</button>
                    <button style={dangerButtonStyle}>Cancelar Assinatura</button>
                  </div>
                </div>
              ) : (
                <div style={emptyStyle}>
                  <p>Você não possui assinatura ativa</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Styles
const containerStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: NETFLIX_COLORS.background,
  color: NETFLIX_COLORS.textPrimary,
  overflowY: 'auto',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: `${NETFLIX_SPACING.xl}px`,
  backgroundColor: NETFLIX_COLORS.surface,
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
};

const backButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: '700',
  margin: 0,
};

const tabsContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '24px',
  padding: `${NETFLIX_SPACING.lg}px ${NETFLIX_SPACING.xl}px`,
  backgroundColor: NETFLIX_COLORS.surface,
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
};

const tabButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  color: NETFLIX_COLORS.textSecondary,
  fontSize: 14,
  fontWeight: '600',
  cursor: 'pointer',
  padding: '8px 0',
  transition: 'all 0.3s ease',
};

const contentStyle: React.CSSProperties = {
  padding: `${NETFLIX_SPACING.xl}px`,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  fontSize: 16,
  color: NETFLIX_COLORS.textSecondary,
};

const emptyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  fontSize: 16,
  color: NETFLIX_COLORS.textSecondary,
  textAlign: 'center',
};

const tableContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const paymentCardStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  border: `1px solid ${NETFLIX_COLORS.border}`,
  borderRadius: '12px',
  padding: `${NETFLIX_SPACING.lg}px`,
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
  paddingBottom: '16px',
  borderBottom: `1px solid ${NETFLIX_COLORS.border}`,
};

const planNameStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.textPrimary,
};

const dateStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  margin: '4px 0 0 0',
};

const statusBadgeStyle = (color: string): React.CSSProperties => ({
  backgroundColor: `${color}20`,
  color: color,
  padding: '6px 12px',
  borderRadius: '20px',
  fontSize: 12,
  fontWeight: '600',
  border: `1px solid ${color}40`,
});

const cardDetailsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '16px',
  marginBottom: '16px',
};

const detailItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: '600',
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textTransform: 'uppercase',
};

const detailValueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: '600',
  color: NETFLIX_COLORS.textPrimary,
  margin: '4px 0 0 0',
};

const cardFooterStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: '16px',
  borderTop: `1px solid ${NETFLIX_COLORS.border}`,
};

const uuidStyle: React.CSSProperties = {
  fontSize: 11,
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  fontFamily: 'monospace',
};

const downloadButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: 12,
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
};

const currentSubscriptionStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  border: `2px solid ${NETFLIX_COLORS.primary}`,
  borderRadius: '16px',
  padding: `${NETFLIX_SPACING.xl}px`,
  maxWidth: '600px',
};

const currentHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '32px',
};

const currentTitleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: '700',
  margin: 0,
  color: NETFLIX_COLORS.primary,
};

const currentDetailsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '24px',
  marginBottom: '32px',
};

const currentDetailStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const currentLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: '600',
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textTransform: 'uppercase',
};

const currentValueStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: '700',
  color: NETFLIX_COLORS.textPrimary,
  margin: 0,
};

const actionButtonsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  flexDirection: 'column',
};

const secondaryButtonStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  color: NETFLIX_COLORS.primary,
  border: `2px solid ${NETFLIX_COLORS.primary}`,
  borderRadius: '8px',
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
};

const dangerButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  color: '#EF4444',
  border: `2px solid #EF4444`,
  borderRadius: '8px',
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
};
